// walletStates.ts — a wallet's state in the operator's words, for every screen
// that shows one (the Wallet register, Party detail).
//
// The database's words are not the operator's (db/0064, 6 Oct 2026):
// 'archived' has meant RETIRED since August — no longer handed out, still
// read — and his "archive", which stops the books reading the address, is the
// status 'unwatched'. No screen shows the database word.
import type { PillTone } from '../components/Pill';

// admin.party_wallets' CHECK, every chain it admits, in its order — SOL since
// db/0023 (missing from the form until 1 Oct 2026: the desk's SOL wallet went
// in by SQL), BASE since db/0044. tests/wallet-door holds this list, and the
// route's (bff lib/wallet-chains.ts), to the live constraint. Party detail's
// add form and the Wallet register both offer it.
export const WALLET_CHAINS = ['TRC20', 'ERC20', 'BEP20', 'SOL', 'BTC', 'ETH', 'TRX', 'BASE'] as const;

// The chains the Books' chain reader reads (bff lib/wallet-chains.ts
// CHAIN_READER_CHAINS; tests/wallet-register holds the two equal). On any other
// chain nothing sent to an address is seen in the Books, whatever its state —
// and the screens must not say otherwise.
export const CHAIN_READER_CHAINS = ['BTC', 'TRC20', 'ERC20', 'BASE'] as const;
export const chainIsRead = (chain: string) => (CHAIN_READER_CHAINS as readonly string[]).includes(chain);

export type WalletStatus = 'active' | 'flagged' | 'archived' | 'unwatched';

export const WALLET_STATE: Record<WalletStatus, { word: string; tone: PillTone; watched: string }> = {
  active:    { word: 'In use',   tone: 'good',    watched: 'watched' },
  flagged:   { word: 'Held',     tone: 'bad',     watched: 'held by screening until someone has looked at it' },
  archived:  { word: 'Retired',  tone: 'neutral', watched: 'still watched — money sent here is still seen' },
  unwatched: { word: 'Archived', tone: 'warn',    watched: 'not watched — money sent here is not seen' },
};

export const walletState = (status: string) =>
  WALLET_STATE[status as WalletStatus] ?? { word: status, tone: 'neutral' as PillTone, watched: '' };

/** Whether the Books see money sent to this address, in words — the state's
 *  own sentence, unless the chain reader does not read the chain at all. */
export const watchWords = (status: string, chain: string): string =>
  chainIsRead(chain) ? walletState(status).watched : `the chain reader does not read ${chain} — nothing sent here is seen in the Books`;

/** What each one-way step does, said before it is taken (the confirm), for an
 *  address on a chain the reader reads; stepWords says it for every chain. */
export const WALLET_STEP_WORDS = {
  retire: 'No longer the address the desk hands out. The chain reader keeps reading it, so money sent here later is still seen; the energy desk stops serving it. This cannot be undone.',
  // What is TRUE (6 Oct 2026). The KYT screening worker and the energy desk
  // keep lists of their own, and both are TOLD: KYT of an archive
  // (routes/kyt.ts unwatchInScreener), the energy desk of a retire or an
  // archive (Energy's feed, and the ten-minute check behind it). When one
  // cannot be told, the answer says so and what happens next.
  archive: 'The Books stop reading this address: the chain reader drops it and the KYT and Energy screens no longer count it, so money sent here later will NOT be seen in the Books. The KYT screening worker is told to stop screening it, and the energy desk to stop serving it. This cannot be undone.',
} as const;

export function stepWords(step: 'retire' | 'archive', chain: string): string {
  if (chainIsRead(chain)) return WALLET_STEP_WORDS[step];
  return step === 'retire'
    ? `No longer the address the desk hands out. The chain reader does not read ${chain}, so nothing sent here is seen in the Books either way. This cannot be undone.`
    : `The chain reader does not read ${chain} anyway; archiving takes the address off the KYT and Energy screens, and the KYT screening worker is told to stop screening it. This cannot be undone.`;
}
